import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import Select from "react-select";
import { selectStyles } from "../helper/SelectStyles";
import {
  getProductById,
  saveProduct,
  updateProduct,
} from "../Apis/ProductAPIs";
import { getCategories } from "../Apis/CategoryAPIs";
import { toast } from "react-toastify";

const ProductFormLayer = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  // Form State
  const [formData, setFormData] = useState({
    productName: "",
    categoryId: "",
    segment: "B2B",
    isActive: true,
  });
  const [categories, setCategories] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [errors, setErrors] = useState({});

  const statusOptions = [
    { value: true, label: "Active" },
    { value: false, label: "Inactive" },
  ];

  const segmentOptions = [
    { value: "B2B", label: "B2B" },
    { value: "B2C", label: "B2C" },
    { value: "Both", label: "Both" },
  ];

  useEffect(() => {
    const fetchInitialData = async () => {
      const catRes = await getCategories();
      if (catRes.status) {
        const cData = catRes.response?.data || catRes.response;
        setCategories(Array.isArray(cData) ? cData : []);
      }

      if (isEdit) {
        const prodRes = await getProductById(id);
        if (prodRes.status && prodRes.response) {
          const item = prodRes.response?.data || prodRes.response;
          let categoryVal = item.categoryId || "";
          if (!categoryVal && item.category) {
            if (typeof item.category === "object") {
              categoryVal = item.category._id || item.category.id;
            } else {
              categoryVal = item.category;
            }
          }
          setFormData({
            productName: item.productName || "",
            categoryId: categoryVal,
            segment: item.segment || "B2B",
            isActive: item.isActive ?? true,
          });
        }
        setLoading(false);
      }
    };
    fetchInitialData();
  }, [isEdit, id]);

  const categoryOptions = categories.map((cat) => ({
    value: cat._id || cat.id,
    label: cat.name,
  }));

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, val) => {
    setFormData((prev) => ({ ...prev, [name]: val ? val.value : "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!formData.productName.trim()) {
      newErrors.productName = "Product Name is Required";
    }

    if (!formData.segment) {
      newErrors.segment = "Segment is Required";
    }

    if (!formData.categoryId) {
      newErrors.categoryId = "Exhibition Type is Required";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setSubmitting(true);
    let res;
    if (isEdit) {
      res = await updateProduct(id, formData);
    } else {
      res = await saveProduct(formData);
    }

    if (res.status) {
      toast.success(`Product ${isEdit ? "Updated" : "Saved"} Successfully`);
      navigate("/products");
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="card h-100 p-0 radius-12">
      <div
        className="card-header border-bottom px-24 py-16 d-flex align-items-center justify-content-between"
        style={{
          background: "linear-gradient(135deg, #000000ff, #bb0007ff, #ff0000ff)",
        }}
      >
        <h6 className="text-white mb-0" style={{ fontWeight: "700" }}>
          {isEdit ? "Edit Product" : "Add Product"}
        </h6>
        <Link
          to="/products"
          className="btn btn-sm d-flex align-items-center gap-1"
          style={{
            backgroundColor: "#f7e7da",
            color: "black",
            fontWeight: "600",
          }}
        >
          <Icon icon="solar:arrow-left-bold" /> List Products
        </Link>
      </div>

      <div className="card-body p-24">
        <form onSubmit={handleSubmit} noValidate>
          <div className="row gy-4">
            {/* Product Name */}
            <div className="col-md-6">
              <label className="form-label fw-bold">
                Product Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                name="productName"
                className={`form-control radius-8 ${errors.productName ? "is-invalid" : ""}`}
                placeholder="Enter Product Name"
                value={formData.productName}
                onChange={handleInputChange}
                autoComplete="off"
              />
              {errors.productName && (
                <div className="invalid-feedback d-block mt-1">
                  {errors.productName}
                </div>
              )}
            </div>

            {/* Category */}
            <div className="col-md-6">
              <label className="form-label fw-bold">Exhibition Type <span className="text-danger">*</span></label>
              <Select
                options={categoryOptions}
                value={categoryOptions.find(
                  (opt) => opt.value === formData.categoryId,
                )}
                onChange={(val) => handleSelectChange("categoryId", val)}
                styles={selectStyles()}
                placeholder="Select Exhibition Type"
              />
              {errors.categoryId && (
                <div className="invalid-feedback d-block mt-1">
                  {errors.categoryId}
                </div>
              )}
            </div>

            {/* Segment */}
            <div className="col-md-6">
              <label className="form-label fw-bold">
                Segment <span className="text-danger">*</span>
              </label>
              <Select
                options={segmentOptions}
                value={segmentOptions.find(
                  (opt) => opt.value === formData.segment,
                )}
                onChange={(val) => handleSelectChange("segment", val)}
                styles={selectStyles()}
                placeholder="Select Segment"
              />
              {errors.segment && (
                <div className="invalid-feedback d-block mt-1">
                  {errors.segment}
                </div>
              )}
            </div>

            {/* Status */}
            <div className="col-md-6">
              <label className="form-label fw-bold">
                Status <span className="text-danger">*</span>
              </label>
              <Select
                options={statusOptions}
                value={statusOptions.find(
                  (opt) => opt.value === formData.isActive,
                )}
                onChange={(val) => handleSelectChange("isActive", val)}
                styles={selectStyles()}
                placeholder="Select Status"
              />
            </div>

            {/* Action Buttons */}
            <div className="col-12 mt-32 d-flex justify-content-end gap-3">
              <button
                type="button"
                className="btn radius-8 px-40 py-11"
                style={{
                  backgroundColor: "white",
                  color: "#d9534f",
                  fontWeight: "700",
                  border: "1px solid #d9534f",
                }}
                onClick={() => navigate("/products")}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn radius-8 px-48 py-11"
                disabled={submitting}
                style={{
                  backgroundColor: "#ed1c24",
                  color: "white",
                  fontWeight: "700",
                }}
              >
                {submitting ? "Saving..." : isEdit ? "Update" : "Save"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductFormLayer;
